import { Outlet, Link, useLocation } from 'react-router-dom'
import {Activity, Calculator, Star, BarChart3, BookOpen} from 'lucide-react'

function Layout() {
  const location = useLocation()
  
  const navigation = [
    { name: 'Home', href: '/', icon: Activity },
    { name: 'Vitality Calculator', href: '/calculator', icon: Calculator },
    { name: 'Top Reviews', href: '/reviews', icon: Star },
    { name: 'Comparison Guides', href: '/comparison', icon: BarChart3 },
    { name: 'Blog', href: '/blog', icon: BookOpen },
  ]

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/"
    return location.pathname === href || location.pathname.startsWith(`${href}/`)
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <Activity className="w-8 h-8 text-teal-600" />
              <span className="text-xl font-bold text-gray-900">Vitamin Vital</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navigation.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${
                      isActive(item.href)
                        ? 'bg-teal-50 text-teal-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.name}</span>
                  </Link>
                )
              })}
            </div>

            {/* CTA Button */}
            <Link
              to="/calculator"
              className="hidden md:block px-6 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors"
            >
              Take Quiz
            </Link>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden py-2 flex items-center space-x-1 overflow-x-auto">
            {navigation.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center space-x-1 ${
                    isActive(item.href)
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  <span>{item.name}</span>
                </Link>
              )
            })}
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Floating CTA (Mobile Only) */}
      {location.pathname !== '/calculator' && (
        <Link
          to="/calculator"
          className="md:hidden fixed bottom-6 right-6 z-40 px-6 py-3 bg-orange-500 text-white rounded-full text-sm font-semibold shadow-lg hover:bg-orange-600 transition-all flex items-center space-x-2 animate-pulse"
        >
          <Calculator className="w-4 h-4" />
          <span>Take the Vitality Quiz</span>
        </Link>
      )}

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Activity className="w-6 h-6 text-teal-600" />
                <span className="text-lg font-bold text-gray-900">Vitamin Vital</span>
              </div>
              <p className="text-sm text-gray-600">
                Your trusted source for personalized vitamin recommendations and expert supplement reviews.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-sm text-gray-600 hover:text-teal-600 transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Disclaimer</h3>
              <p className="text-xs text-gray-500">
                This calculator is for informational purposes and is not medical advice. Consult a doctor before starting new supplements.
              </p>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              © {new Date().getFullYear()} Vitamin Vital Portal. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Layout
